import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { AppBaseComponent } from "@app/shared/components/app-base.component";
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { MatIconModule } from "@angular/material/icon";
import { trigger, state, style, transition, animate } from "@angular/animations";
import { DocMeta } from "@template-ui/decorators";
import { MoreLessTogglerComponent } from "../more-less-toggler/more-less-toggler.component";
import { toggle } from "@template-ui/helpers";
import { SkeletonModule } from "primeng/skeleton";


/**
 * Displays a collapsible case summary bar showing workflow name, step, status,
 * and key case data fields. The bar is driven by `bitsState.caseSummarySignal()`.
 */
@DocMeta({
  title: "Case Summary Bar",
  category: "Components",
  showcaseComponentNames: ["CaseSummaryBarSampleComponent"],
})
@Component({
    selector: 'app-case-summary-bar',
    templateUrl: './case-summary-bar.component.html',
    styleUrl: './case-summary-bar.component.scss',
    imports: [
        CommonModule,
        UiLibraryModule,
        MatIconModule,
        MoreLessTogglerComponent,
        SkeletonModule
    ],
    animations: [
        trigger("fadeInOut", [
            state("void", style({ opacity: 0, transform: "translateY(-20px)" })),
            transition("void <=> *", [animate("500ms 500ms ease-in-out")])
        ]),
        trigger("slideInOut", [
            state("collapsed", style({ height: "0px", opacity: 0, padding: 0 })),
            state("expanded", style({
                height: "*",
                opacity: 1,
                padding: "16px",
                paddingTop: 0
            })),
            transition("collapsed <=> expanded", [
                animate("300ms ease")
            ])
        ])
    ]
})
export class CaseSummaryBarComponent extends AppBaseComponent {

  // ───────────────────────────────────────────────────────────────
  //  Inputs, Outputs, Models, and Public Signals
  // ───────────────────────────────────────────────────────────────

  /**
   * Explicit case ID. When omitted the bar uses the ID from the current route via `idSignal()`.
   * Pass a value when embedding the bar outside a routed investigation context (e.g. showcase).
   */
  caseId = input<number | string>();

  // ───────────────────────────────────────────────────────────────
  //  Computed Signals / Derived State
  // ───────────────────────────────────────────────────────────────

  /** Resolved summary model from shared state. */
  protected readonly workflowSummaryModelSig = computed(() => this.bitsState.caseSummarySignal());

  // ───────────────────────────────────────────────────────────────
  //  Public Methods (Template-Bound Handlers)
  // ───────────────────────────────────────────────────────────────

  /** Toggles the expanded/collapsed state of the summary bar details. */
  protected toggleCommonHeaderDetails(): void {
    toggle(this.bitsState.summaryBarExpanded);
  }

}
